import { useDispatch, useSelector } from "react-redux";
import { selectToken } from "../app/slices/AuthenticationSlice";
import { selectCurrentEnvironmentId } from "../app/slices/EnvironmentSlice";
import { setIsLoading } from "../app/slices/GlobalSlice";
import {
  selectCurrentTrainingId,
  setCurrentTrainingId,
} from "../app/slices/ProjectsSlice";
import {
  selectLocalizedLanguages,
  selectTrainingLocalizationState,
} from "../app/slices/TrainingLocalizationSlice";
import {
  selectActors,
  selectAllPropPresets,
  selectDisableProgressBar,
  selectReferenceLanguage,
  selectSelectedTake,
  selectSelectedTake3D,
  selectSelectedEnvironmentLayerIds,
  selectCustomSequenceVariables,
  selectTakes360,
  selectTakes3D,
  selectTrainingDescription,
  selectTrainingName,
  selectTrainingType,
  setTrainingName,
} from "../app/slices/TrainingSlice";
import {
  markCurrentDocumentSaved,
  rebaseCurrentDocument,
  selectSavedCheckpoint,
} from "../app/slices/HistorySlice";
import { selectCurrentOrganization } from "../app/slices/UserSlice";
import { Training } from "../models/Training";
import { TrainingService } from "../services/TrainingService";
import { UseSerializeLocalization } from "./SerializeLocalizationHook";
import { createSetupTake3D } from "../helpers/TestFromTakeSetup";

type SaveTrainingMode = "manual" | "autosave" | "publish";

type SaveTrainingOptions = {
  mode?: SaveTrainingMode;
  showGlobalLoading?: boolean;
};

type SaveTrainingResult = {
  success: boolean;
  trainingId?: string;
};

export const useSaveTraining = () => {
  const dispatch = useDispatch();
  const takes = useSelector(selectTakes360);
  const takes3d = useSelector(selectTakes3D);
  const selectedTake3d = useSelector(selectSelectedTake3D);
  const selectedTake360 = useSelector(selectSelectedTake);
  const currentTrainingId = useSelector(selectCurrentTrainingId);
  const trainingDescription = useSelector(selectTrainingDescription);
  const trainingName = useSelector(selectTrainingName);
  const savedCheckpoint = useSelector(selectSavedCheckpoint);
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const token = useSelector(selectToken);
  const locState = useSelector(selectTrainingLocalizationState);
  const refLanguage = useSelector(selectReferenceLanguage);
  const languages = useSelector(selectLocalizedLanguages);
  const { serializeLanguage } = UseSerializeLocalization();
  const trainingType = useSelector(selectTrainingType);
  const actors = useSelector(selectActors);
  const props = useSelector(selectAllPropPresets);
  const customSequenceVariables = useSelector(selectCustomSequenceVariables);
  const selectedLayerIds = useSelector(selectSelectedEnvironmentLayerIds);
  const disableProgressBar = useSelector(selectDisableProgressBar);

  const currentEnvironmentId = useSelector(selectCurrentEnvironmentId);

  var targetVersion = Number(process.env.REACT_APP_PLAYIT_TRAINING_VERSION);

  const resolveReferenceLanguage = () => {
    if (refLanguage !== "") return refLanguage;
    return languages[0]?.shortcode ?? "";
  };
  const saveTraining = async (
    options?: SaveTrainingOptions
  ): Promise<SaveTrainingResult> => {
    if (!orgId || !token || currentTrainingId === "") return { success: false };
    const shouldShowGlobalLoading = options?.showGlobalLoading ?? true;
    if (shouldShowGlobalLoading) {
      dispatch(setIsLoading(true));
    }

    try {
      const metadataSaved = await TrainingService.UpdateTrainingName(
        orgId,
        currentTrainingId,
        token,
        trainingName,
        trainingDescription
      );
      if (!metadataSaved) return { success: false };

      const content = JSON.stringify(getSerializedTraining());

      // saving training content
      const saveTrainingVersionSuccess =
        await TrainingService.CreateNewTrainingVersion(
          orgId,
          token,
          currentTrainingId,
          content
        );

      if (!saveTrainingVersionSuccess) {
        return { success: false };
      }

      // we need to do this for each language thats not created yet
      for (var i = 0; i < locState.localizedLanguages.length; i++) {
        var data = JSON.stringify(
          serializeLanguage(locState.localizedLanguages[i].shortcode)
        );
        const updateTranslationSuccess =
          await TrainingService.UpdateTrainingTranslation(
            orgId,
            token,
            currentTrainingId,
            locState.localizedLanguages[i].shortcode,
            data
          );

        if (!updateTranslationSuccess) {
          return { success: false };
        }

        const includeStatus =
          await TrainingService.UpdateTranslationIncludeInPublish(
            orgId,
            token,
            currentTrainingId,
            locState.localizedLanguages[i].shortcode,
            locState.localizedLanguages[i].includeInPublish ?? true
          );
        if (includeStatus < 200 || includeStatus >= 300) {
          return { success: false };
        }
      }

      const currentLanguageIds = new Set(
        locState.localizedLanguages.map((language) => language.shortcode)
      );
      const deletedLanguageIds =
        savedCheckpoint?.trainingLocalization.localizedLanguages
          .map((language) => language.shortcode)
          .filter((shortcode) => !currentLanguageIds.has(shortcode)) ?? [];

      for (const shortcode of deletedLanguageIds) {
        const deleted = await TrainingService.DeleteTrainingTranslation(
          orgId,
          token,
          currentTrainingId,
          shortcode
        );
        if (!deleted) return { success: false };
      }

      dispatch(markCurrentDocumentSaved());
      return { success: true };
    } finally {
      if (shouldShowGlobalLoading) {
        dispatch(setIsLoading(false));
      }
    }
  };

  const saveTrainingAs = async (
    newName: string,
    options?: SaveTrainingOptions
  ): Promise<SaveTrainingResult> => {
    const trimmedName = newName.trim();
    if (!orgId || !token || trimmedName === "") return { success: false };

    const shouldShowGlobalLoading = options?.showGlobalLoading ?? true;
    if (shouldShowGlobalLoading) {
      dispatch(setIsLoading(true));
    }

    let newTrainingId: string | undefined;

    try {
      const createdTraining = await TrainingService.CreateTraining(
        orgId,
        token,
        trimmedName,
        trainingDescription,
        trainingType
      );

      if (!createdTraining?.id) {
        return { success: false };
      }

      newTrainingId = createdTraining.id;

      const saveTrainingVersionSuccess =
        await TrainingService.CreateNewTrainingVersion(
          orgId,
          token,
          newTrainingId,
          JSON.stringify(getSerializedTraining())
        );

      if (!saveTrainingVersionSuccess) {
        await TrainingService.DeleteTraining(orgId, token, newTrainingId);
        return { success: false };
      }

      for (var i = 0; i < locState.localizedLanguages.length; i++) {
        var data = JSON.stringify(
          serializeLanguage(locState.localizedLanguages[i].shortcode)
        );
        const updateTranslationSuccess =
          await TrainingService.UpdateTrainingTranslation(
            orgId,
            token,
            newTrainingId,
            locState.localizedLanguages[i].shortcode,
            data
          );

        if (!updateTranslationSuccess) {
          await TrainingService.DeleteTraining(orgId, token, newTrainingId);
          return { success: false };
        }

        const includeStatus =
          await TrainingService.UpdateTranslationIncludeInPublish(
            orgId,
            token,
            newTrainingId,
            locState.localizedLanguages[i].shortcode,
            locState.localizedLanguages[i].includeInPublish ?? true
          );
        if (includeStatus < 200 || includeStatus >= 300) {
          await TrainingService.DeleteTraining(orgId, token, newTrainingId);
          return { success: false };
        }
      }

      dispatch(setCurrentTrainingId(newTrainingId));
      dispatch(setTrainingName(trimmedName));
      dispatch(rebaseCurrentDocument());
      return { success: true, trainingId: newTrainingId };
    } finally {
      if (shouldShowGlobalLoading) {
        dispatch(setIsLoading(false));
      }
    }
  };

  const getSerializedTraining = () => {
    const resolvedReferenceLanguage = resolveReferenceLanguage();

    if (trainingType === 0) {
      var training3d: Training = {
        refLanguage: resolvedReferenceLanguage,
        takes: takes3d,
        trainingType: 0,
        version: targetVersion - 1,
        metadata: {
          environmentId: currentEnvironmentId,
          actors: actors,
          props: props,
          selectedLayerIds: selectedLayerIds,
          sequenceVariables: customSequenceVariables,
          disableProgressBar: disableProgressBar,
        },
      };
      return training3d;
    } else if (trainingType === 1) {
      var training: Training = {
        refLanguage: resolvedReferenceLanguage,
        trainingType: 1,
        takes: takes,
        version: targetVersion,
        metadata: {
          sequenceVariables: customSequenceVariables,
          disableProgressBar: disableProgressBar,
        },
      };
      return training;
    }
  };

  const getSerializedTrainingSingleTake = () => {
    const resolvedReferenceLanguage = resolveReferenceLanguage();

    if (trainingType === 0) {
      var training3d: Training = {
        refLanguage: resolvedReferenceLanguage,
        takes: [selectedTake3d!],
        trainingType: 0,
        version: targetVersion - 1,
        metadata: {
          environmentId: currentEnvironmentId,
          actors: actors,
          props: props,
          selectedLayerIds: selectedLayerIds,
          sequenceVariables: customSequenceVariables,
          disableProgressBar: disableProgressBar,
        },
      };
      return training3d;
    } else {
      var training: Training = {
        refLanguage: resolvedReferenceLanguage,
        trainingType: 1,
        takes: [selectedTake360!],
        version: targetVersion,
        metadata: {
          sequenceVariables: customSequenceVariables,
          disableProgressBar: disableProgressBar,
        },
      };
      return training;
    }
  };

  const getSerializedTrainingFromCurrentTake = () => {
    const resolvedReferenceLanguage = resolveReferenceLanguage();

    if (trainingType === 0) {
      const startIndex = takes3d.findIndex((t) => t.id === selectedTake3d!.id);
      const trainingTakes = takes3d.slice(startIndex);
      if (trainingTakes.length > 0)
        trainingTakes.unshift(createSetupTake3D(trainingTakes[0]));
      var training3d: Training = {
        refLanguage: resolvedReferenceLanguage,
        takes: trainingTakes,
        trainingType: 0,
        version: targetVersion - 1,
        metadata: {
          environmentId: currentEnvironmentId,
          actors: actors,
          props: props,
          selectedLayerIds: selectedLayerIds,
          sequenceVariables: customSequenceVariables,
          disableProgressBar: disableProgressBar,
        },
      };
      return training3d;
    } else {
      const startIndex = takes.findIndex((t) => t.id === selectedTake360!.id);
      const trainingTakes = takes.slice(startIndex);

      var training: Training = {
        refLanguage: resolvedReferenceLanguage,
        trainingType: 1,
        takes: trainingTakes,
        version: targetVersion,
        metadata: {
          sequenceVariables: customSequenceVariables,
          disableProgressBar: disableProgressBar,
        },
      };
      return training;
    }
  };

  return {
    saveTraining,
    saveTrainingAs,
    getSerializedTraining,
    getSerializedTrainingSingleTake,
    getSerializedTrainingFromCurrentTake,
  } as const;
};
